import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatPrice, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SHIPPING = [
  { id: "standard", name: "Envío estándar (3-5 días)", price: 4.95, free: 150 },
  { id: "express", name: "Envío exprés (24h)", price: 9.95, free: 150 },
  { id: "premium", name: "Premium guante blanco", price: 19.95, free: 150 },
  { id: "pickup", name: "Recogida en boutique París", price: 0 },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [shipping, setShipping] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // 'stripe' | 'redsys'
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "España",
    notes: "",
  });

  useEffect(() => {
    if (items.length === 0) nav("/shop");
  }, [items.length, nav]);

  const sh = SHIPPING.find(s => s.id === shipping);
  const shippingCost = sh.free && subtotal >= sh.free ? 0 : sh.price;
  const discountAmount = coupon?.valid ? coupon.discount_amount : 0;
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);
  const tax = Math.round(discountedSubtotal * 0.21 * 100) / 100;
  const total = discountedSubtotal + shippingCost + tax;

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim(),
        subtotal,
        email: form.email || user?.email || "",
      });
      if (data.valid) {
        setCoupon(data);
        toast.success(`Cupón aplicado: -${data.discount_percent}% (${data.discount_amount.toFixed(2)}€)`);
      } else {
        setCoupon(null);
        toast.error(data.reason || "Cupón no válido");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const orderRes = await api.post("/orders", {
        items: items.map(i => ({
          product_id: i.product_id, name: i.name, price: i.price,
          quantity: i.quantity, image: i.image, sku: i.sku,
        })),
        shipping_address: {
          full_name: form.full_name, phone: form.phone,
          address_line1: form.address_line1, address_line2: form.address_line2,
          city: form.city, state: form.state, postal_code: form.postal_code,
          country: form.country,
        },
        shipping_method: shipping,
        notes: form.notes,
        coupon_code: coupon?.valid ? coupon.code : "",
      });
      const order = orderRes.data;
      // store order id so success page can pick it up
      localStorage.setItem("luxe-pending-order", order.id);

      if (paymentMethod === "redsys") {
        const { data } = await api.post("/payments/redsys/initiate", {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        // Build and auto-submit a form to Redsys
        const f = document.createElement("form");
        f.method = "POST";
        f.action = data.action_url;
        f.style.display = "none";
        for (const [k, v] of Object.entries({
          Ds_SignatureVersion: data.Ds_SignatureVersion,
          Ds_MerchantParameters: data.Ds_MerchantParameters,
          Ds_Signature: data.Ds_Signature,
        })) {
          const i = document.createElement("input");
          i.type = "hidden";
          i.name = k;
          i.value = v;
          f.appendChild(i);
        }
        document.body.appendChild(f);
        f.submit();
        return;
      }

      // Default: Stripe
      const payRes = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = payRes.data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="pt-28 min-h-screen bg-carbon">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <p className="overline mb-4">Checkout</p>
        <h1 className="font-heading text-4xl md:text-5xl text-ivory font-light tracking-tighter mb-12">Finalizar compra</h1>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {/* Contact */}
            <section>
              <h2 className="font-heading text-2xl text-ivory mb-6">1. Datos de contacto</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="luxe-label">Nombre completo</label>
                  <input data-testid="input-fullname" required value={form.full_name} onChange={e => change("full_name", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">Email</label>
                  <input data-testid="input-email" type="email" required value={form.email} onChange={e => change("email", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">Teléfono</label>
                  <input data-testid="input-phone" required value={form.phone} onChange={e => change("phone", e.target.value)} className="luxe-input" />
                </div>
              </div>
            </section>

            {/* Address */}
            <section>
              <h2 className="font-heading text-2xl text-ivory mb-6">2. Dirección de envío</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="luxe-label">Dirección</label>
                  <input data-testid="input-address" required value={form.address_line1} onChange={e => change("address_line1", e.target.value)} className="luxe-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="luxe-label">Piso, puerta (opcional)</label>
                  <input value={form.address_line2} onChange={e => change("address_line2", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">Ciudad</label>
                  <input data-testid="input-city" required value={form.city} onChange={e => change("city", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">Provincia</label>
                  <input data-testid="input-state" required value={form.state} onChange={e => change("state", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">Código Postal</label>
                  <input data-testid="input-postal" required value={form.postal_code} onChange={e => change("postal_code", e.target.value)} className="luxe-input" />
                </div>
                <div>
                  <label className="luxe-label">País</label>
                  <input value={form.country} onChange={e => change("country", e.target.value)} className="luxe-input" />
                </div>
              </div>
            </section>

            {/* Shipping */}
            <section>
              <h2 className="font-heading text-2xl text-ivory mb-6">3. Envío</h2>
              <div className="space-y-3" data-testid="shipping-options">
                {SHIPPING.map(s => {
                  const free = s.free && subtotal >= s.free;
                  return (
                    <label key={s.id} className={`flex items-center justify-between p-4 border cursor-pointer ${shipping === s.id ? "border-gold" : "border-smoke"}`}>
                      <div className="flex items-center gap-4">
                        <input type="radio" name="shipping" checked={shipping === s.id} onChange={() => setShipping(s.id)} className="accent-gold" data-testid={`shipping-${s.id}`} />
                        <span className="text-ivory">{s.name}</span>
                      </div>
                      <span className="text-gold font-heading">{free ? "Gratis" : (s.price === 0 ? "Gratis" : formatPrice(s.price))}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Notes */}
            <section>
              <label className="luxe-label">Notas para el pedido (opcional)</label>
              <textarea value={form.notes} onChange={e => change("notes", e.target.value)} className="luxe-input border border-smoke px-3 py-3 min-h-[80px]" />
            </section>

            {/* Payment method */}
            <section>
              <h2 className="font-heading text-2xl text-ivory mb-6">4. Método de pago</h2>
              <div className="space-y-3" data-testid="payment-methods">
                <label className={`flex items-center justify-between p-4 border cursor-pointer ${paymentMethod === "stripe" ? "border-gold" : "border-smoke"}`}>
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMethod === "stripe"}
                      onChange={() => setPaymentMethod("stripe")}
                      className="accent-gold"
                      data-testid="pay-stripe"
                    />
                    <div>
                      <span className="text-ivory block">Tarjeta (Stripe)</span>
                      <span className="text-ivory/50 text-xs">Visa · Mastercard · Apple Pay · Google Pay</span>
                    </div>
                  </div>
                  <span className="text-gold font-heading text-sm">SSL</span>
                </label>
                <label className={`flex items-center justify-between p-4 border cursor-pointer ${paymentMethod === "redsys" ? "border-gold" : "border-smoke"}`}>
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMethod === "redsys"}
                      onChange={() => setPaymentMethod("redsys")}
                      className="accent-gold"
                      data-testid="pay-redsys"
                    />
                    <div>
                      <span className="text-ivory block">TPV Bancario (Redsys)</span>
                      <span className="text-ivory/50 text-xs">Pago seguro con tarjeta española · 3D Secure</span>
                    </div>
                  </div>
                  <span className="text-gold font-heading text-sm">TPV</span>
                </label>
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="bg-graphite p-8 border border-smoke h-fit sticky top-32" data-testid="checkout-summary">
            <h2 className="font-heading text-2xl text-ivory mb-6">Resumen</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto mb-6">
              {items.map(item => (
                <div key={item.product_id} className="flex gap-3">
                  <div className="w-14 h-16 flex-shrink-0 overflow-hidden bg-smoke">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="text-ivory">{item.name}</p>
                    <p className="text-ivory/50 text-xs mt-1">x{item.quantity}</p>
                  </div>
                  <p className="text-gold text-sm">{formatPrice(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-smoke pt-4 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-ivory/60">Subtotal</span><span className="text-ivory">{formatPrice(subtotal)}</span></div>
              {coupon?.valid && (
                <div className="flex justify-between text-sm" data-testid="discount-line">
                  <span className="text-gold flex items-center gap-2">
                    Cupón {coupon.code} (-{coupon.discount_percent}%)
                    <button type="button" onClick={removeCoupon} className="text-ivory/40 hover:text-rose text-xs">✕</button>
                  </span>
                  <span className="text-gold">−{formatPrice(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm"><span className="text-ivory/60">Envío</span><span className="text-ivory">{shippingCost === 0 ? "Gratis" : formatPrice(shippingCost)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-ivory/60">IVA (21%)</span><span className="text-ivory">{formatPrice(tax)}</span></div>
              <div className="flex justify-between font-heading text-2xl pt-3 border-t border-smoke mt-3"><span className="text-ivory">Total</span><span className="text-gold" data-testid="total-amount">{formatPrice(total)}</span></div>
            </div>

            {/* Coupon input */}
            {!coupon?.valid && (
              <div className="mt-5 pt-5 border-t border-smoke">
                <label className="luxe-label">¿Tiene un cupón?</label>
                <div className="flex gap-2">
                  <input
                    data-testid="coupon-input"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="BDAYXXXXXX"
                    className="luxe-input border border-smoke px-3 py-2 flex-1 font-mono"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    data-testid="apply-coupon"
                    className="btn-outline-gold !py-2 !px-4 disabled:opacity-50"
                  >
                    {couponLoading ? "..." : "Aplicar"}
                  </button>
                </div>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="pay-button" className="btn-gold w-full mt-6 disabled:opacity-50">
              {loading ? "Procesando..." : (paymentMethod === "redsys" ? "Pagar con TPV Bancario" : "Pagar con Stripe")}
            </button>
            <p className="text-xs text-ivory/40 mt-3 text-center">Pago seguro · Encriptado SSL</p>
          </aside>
        </form>
      </div>
    </div>
  );
}
